import api from '@disco/api/server'
import memoize from 'memoize-one'

import React, { useEffect, useState } from 'react'
import type { PlaylistId } from 'types/playlist'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import ModalDialog from 'components/ui/modal-dialog'

const memoizeCb = async (input: string) => {
  if (_abortController) {
    _abortController.abort()
  }

  _abortController = new AbortController()
  const response = await api.getSearchResultsByQuery(
    undefined,
    {
      type: 'playlist',
      q: input,
    },
    undefined,
    _abortController.signal,
  )
  return response.results
}

// memoize function to not trigger api call when input doesn't change
let fetchPlaylists = memoize(memoizeCb)
let _abortController: AbortController | null | undefined = null
type ItemType = {
  value: PlaylistId
  label: string
  disabled: boolean
}
type Props = {
  checkForApiUpdates?: boolean
  getOptions: (...args: Array<any>) => any
  submitButtonCaption: string
  title: string
  onClose: (...args: Array<any>) => any
  onSubmit: (playlistIds: PlaylistId[]) => void
  multi?: boolean
}

const PlaylistSelectionDialog = ({
  checkForApiUpdates,
  getOptions,
  submitButtonCaption,
  title,
  onClose,
  onSubmit,
  multi = false,
}: Props): React.ReactNode => {
  const [playlists, setPlaylists] = useState<ItemType[]>([])
  useEffect(() => {
    if (checkForApiUpdates) {
      // memoize function to not trigger api call when input doesn't change.
      // After opening and closing the PlaylistSelectionDialog component, the data from the api can change,
      // so we always make a request at the first render to get the actual data.
      fetchPlaylists = memoize(memoizeCb)
    }
  }, [])
  return (
    <ModalDialog
      title={title}
      primaryAction={{
        caption: submitButtonCaption,
        disabled: !playlists.length,
        onClick: () => {
          if (playlists.length) onSubmit(playlists.map(({ value }) => value))
        },
      }}
      onCancel={onClose}
    >
      <Label caption="Playlist" />
      <CustomDropdown
        multi={multi}
        async
        autoFocus
        loadOptions={async (input, callback) => {
          let playlists = []

          try {
            playlists = await fetchPlaylists(input)
          } catch (_) {}

          return getOptions({
            input,
            callback,
            playlists,
          })
        }}
        value={multi ? playlists : playlists[0]}
        onChange={p => setPlaylists(multi ? p : [p])}
        placeholder="Choose playlist"
      />
    </ModalDialog>
  )
}

export default PlaylistSelectionDialog
